import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, map, shareReplay, tap } from 'rxjs';

import { Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { ON_BEHALF_OF_QUERY_PARAM, RESET_ON_BEHALF_OF_QUERY_PARAM } from './impersonation.consts';

@Injectable({ providedIn: 'root' })
export class ImpersonationUrlListenerService {
    #router = inject(Router);
    #window: Window = inject(DOCUMENT).defaultView as Window;
    #logger = inject(Logger).createCustomLogger('Impersonation listener');

    public readonly url$ = this.#router.events.pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        map(event => new URL(event.url, this.#window.location.origin)),
        map(url => {
            // order is important, after the first branch, we know the RESET_ON_BEHALF is absent
            if (urlResetImpersonatedUser(url)) {
                return [RESET_ON_BEHALF_OF_QUERY_PARAM, url] as const;
            } else if (urlHasImpersonatedUser(url)) {
                return [IMPERSONATE_ACTION, url] as const;
            } else if (emptyOnBehalf(url)) {
                return [ON_BEHALF_OF_QUERY_PARAM, url] as const;
            }
            return [UNKNOWN_ACTION, url] as const;
        }),
        filter(([action, _]) => action !== 'unknown'),
        shareReplay(1),
        tap(([action, url]) => this.#logger.info(`URL impersonation: ${action}, url: ${url}`))
    );
}

const urlHasImpersonatedUser = (url: URL) => !!url.searchParams.get(ON_BEHALF_OF_QUERY_PARAM);
const urlResetImpersonatedUser = (url: URL) => url.searchParams.has(RESET_ON_BEHALF_OF_QUERY_PARAM);
const emptyOnBehalf = (url: URL) =>
    url.searchParams.has(ON_BEHALF_OF_QUERY_PARAM) && !url.searchParams.get(ON_BEHALF_OF_QUERY_PARAM);

const UNKNOWN_ACTION = 'unknown' as const;
export const IMPERSONATE_ACTION = 'impersonate' as const;
